import * as React from "react";
import { Controller, Resolver, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ChevronDown, WandSparkles } from "lucide-react";
import { Button, Collapsible, Combobox, Dialog, Field, Input, Spinner, Textarea } from "@/components/ui";
import { EligibilityEditor } from "@/containers/Coach/EligibilityEditor";
import { Category } from "@/interfaces/models/Categories";
import { CategoriesServices, CoachService, GenericAccess } from "@/services";
import { AiCaptionServices } from "@/services/bedrock/AiCaptionServices";
import { isApiError } from "@/services/bedrock/apiError";
import { DesignPackageDetail } from "@/services/bedrock/dtos/DesignPackage";
import { EligibilityCriterion } from "@/services/bedrock/dtos/EligibilityCriterion";
import { useCategories } from "@/utils/useCategories";
import { useSocialCoachSession } from "@/utils/useSocialCoachSession";
import { inFlightMessage, useDesignPackageMutations } from "../hooks/useDesignPackageMutations";

interface PublishDialogProps {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  pkg: DesignPackageDetail;
  onPublished: (pkg: DesignPackageDetail) => void;
}

const FORM_ID = "publish-design-form";

/** What the AI gets: the request that made the design, framed as the post this caption goes with. */
export function captionInputFor(pkg: Pick<DesignPackageDetail, "instructions" | "name">): string {
  const request = pkg.instructions?.trim();
  return [
    "Write a social media caption for a post that shares this design.",
    "Write about what the post says to the reader, not about how the image looks.",
    request ? `The design was created from this request: "${request}"` : `The design is called "${pkg.name}".`,
  ].join(" ");
}

const schema = z.object({
  title: z.string().trim().min(1, "Add a title.").max(120, "Keep the title under 120 characters."),
  caption: z.string().max(2000, "Keep the caption under 2,000 characters."),
  categories: z.array(z.custom<Category>()),
  eligibilityCriteria: z.array(z.custom<EligibilityCriterion>()),
});

type PublishFormValues = z.infer<typeof schema>;

export const PublishDialog: React.FC<PublishDialogProps> = ({ open, onOpenChange, pkg, onPublished }) => {
  const { coachId: sessionCoachId } = useSocialCoachSession();
  const coachId = pkg.coachId ?? sessionCoachId;
  const { categories, clearCategoryCache } = useCategories("LIBRARY", coachId ?? undefined);
  const { publish } = useDesignPackageMutations(pkg.id);
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  // New categories have to be in `items` too, or the Combobox drops them from the value.
  const [addedCategories, setAddedCategories] = React.useState<Category[]>([]);
  const categoryItems = React.useMemo(() => [...categories, ...addedCategories], [categories, addedCategories]);
  const [currentAccess, setCurrentAccess] = React.useState<GenericAccess>({ eligibilityCriteria: [] });
  const originalAccess = React.useMemo<GenericAccess>(() => ({ eligibilityCriteria: [] }), []);
  // Review designs get a caption written for each post from its reviews; every other design starts
  // its posts from the prompt's caption, like a regular prompt.
  const needsCaption = pkg.contextType !== "review";
  const [generating, setGenerating] = React.useState(false);
  const [generateFailed, setGenerateFailed] = React.useState(false);

  const form = useForm<PublishFormValues>({
    resolver: zodResolver(schema) as Resolver<PublishFormValues>,
    mode: "onTouched",
    defaultValues: { title: pkg.name, caption: "", categories: [], eligibilityCriteria: [] },
  });

  // Each opening starts over from the package's name; a rename while the dialog is open doesn't
  // overwrite what the coach typed.
  const { reset } = form;
  React.useEffect(() => {
    if (!open) return;
    reset({ title: pkg.name, caption: "", categories: [], eligibilityCriteria: [] });
    setCurrentAccess({ eligibilityCriteria: [] });
    setAddedCategories([]);
    setSubmitError(null);
  }, [open, reset]);

  /** `replace` overwrites the field; otherwise a caption the coach started typing meanwhile is kept. */
  const generateCaption = React.useCallback(
    async (replace: boolean) => {
      setGenerating(true);
      setGenerateFailed(false);
      try {
        const { caption } = await AiCaptionServices.generate({
          input: captionInputFor(pkg),
          designPackageId: pkg.id,
        });
        if (replace || !form.getValues("caption").trim()) {
          form.setValue("caption", caption, { shouldValidate: form.formState.isSubmitted, shouldDirty: true });
          form.clearErrors("caption");
        }
      } catch {
        setGenerateFailed(true);
      } finally {
        setGenerating(false);
      }
    },
    [pkg.id, pkg.instructions, pkg.name, form]
  );

  // Each opening starts with a caption written from the request that made the design.
  React.useEffect(() => {
    if (open && needsCaption) void generateCaption(false);
  }, [open, needsCaption]);

  const criteria = form.watch("eligibilityCriteria");
  const eligibilitySummary =
    criteria.length === 0 ? "Everyone on your team" : `${currentAccess.eligiblePlayerIds?.length ?? 0} players`;

  const updateEligibility = async (next: EligibilityCriterion[]) => {
    form.setValue("eligibilityCriteria", next);
    if (coachId == null) return;
    const access = await CoachService.getAccessPreview(coachId, "LIBRARY", next);
    setCurrentAccess(access);
  };

  const submit = async (values: PublishFormValues) => {
    setSubmitError(null);
    try {
      const categoryIds: number[] = [];
      let added = false;
      for (const category of values.categories) {
        if (!category.categoryId || Number(category.categoryId) < 0) {
          added = true;
          const result = await CategoriesServices.add(coachId!, { name: category.name.trim() }, "LIBRARY");
          categoryIds.push(Number(result.categoryId));
        } else {
          categoryIds.push(Number(category.categoryId));
        }
      }
      if (added) clearCategoryCache();

      const published = await publish.mutateAsync({
        title: values.title.trim(),
        // Left blank, a re-publish keeps the caption the prompt already had.
        ...(needsCaption && values.caption.trim() ? { message: values.caption.trim() } : {}),
        categoryIds,
        eligibilityCriteria: values.eligibilityCriteria,
      });
      onOpenChange(false);
      onPublished(published);
    } catch (error) {
      if (isApiError(error, "not_completed")) setSubmitError("Finish generating before publishing.");
      else if (isApiError(error, "refine_in_progress")) {
        const actor = (error.details as { actor?: { name: string } } | undefined)?.actor;
        setSubmitError(actor ? inFlightMessage(actor.name) : "Wait for the current change to finish.");
      }
    }
  };

  const submitting = form.formState.isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <Dialog.Header>
        <Dialog.Title className="text-xl font-bold font-sans">Publish to your players</Dialog.Title>
        <Dialog.Description className="text-sm text-slate-500">
          This creates a prompt in your library. Players see it as a personalized post they can share.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="max-h-[70vh] overflow-y-auto">
        <form id={FORM_ID} className="flex flex-col gap-4 py-2" onSubmit={form.handleSubmit(submit)}>
          <Controller
            control={form.control}
            name="title"
            render={({ field, fieldState }) => (
              <Field label="Prompt title" error={fieldState.error?.message}>
                {controlProps => <Input {...controlProps} {...field} />}
              </Field>
            )}
          />
          {needsCaption && (
            <Controller
              control={form.control}
              name="caption"
              render={({ field, fieldState }) => (
                <Field label="Prompt Caption (optional)" error={fieldState.error?.message}>
                  {controlProps => (
                    <div className="flex flex-col gap-2">
                      {generating && !field.value ? (
                        <div
                          role="status"
                          className="flex min-h-[84px] items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-500"
                        >
                          <Spinner className="size-4" />
                          Writing a caption from your design…
                        </div>
                      ) : (
                        <Textarea
                          {...controlProps}
                          {...field}
                          autoSize
                          minRows={3}
                          maxRows={8}
                          placeholder="Write the caption players start with"
                        />
                      )}
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-xs text-slate-500">
                          {generateFailed
                            ? "Couldn't write a caption. Write your own or try again."
                            : "Players start with this caption and can edit it before posting, like a regular prompt."}
                        </p>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          loading={generating}
                          onClick={() => void generateCaption(true)}
                        >
                          <WandSparkles aria-hidden="true" />
                          Generate
                        </Button>
                      </div>
                    </div>
                  )}
                </Field>
              )}
            />
          )}
          <Controller
            control={form.control}
            name="categories"
            render={({ field }) => (
              <Field label="Categories (optional)">
                <Combobox<Category, true>
                  items={categoryItems}
                  value={field.value}
                  onValueChange={value => field.onChange(value ?? [])}
                  isItemEqualToValue={(a, b) => a.categoryId === b.categoryId && a.name === b.name}
                  itemToStringValue={category => String(category.categoryId ?? category.name)}
                  itemToStringLabel={category => category.name}
                  onAddItem={name => {
                    const added = { categoryId: -Date.now(), name };
                    setAddedCategories(current => [...current, added]);
                    return added;
                  }}
                  placeholder="Add categories"
                  emptyText="No categories found."
                  multiple
                  searchable
                  allowAdditions
                />
              </Field>
            )}
          />

          <Collapsible className="rounded-xl border border-slate-200">
            <Collapsible.Trigger className="group flex w-full cursor-pointer items-center justify-between gap-2 p-3 text-left">
              <span className="flex flex-col">
                <span className="text-sm font-semibold text-slate-800">Who receives this?</span>
                <span className="text-xs text-slate-500" data-testid="eligibility-summary">
                  {eligibilitySummary}
                </span>
              </span>
              <ChevronDown className="size-4 text-slate-400 transition-transform group-data-[panel-open]:rotate-180" />
            </Collapsible.Trigger>
            <Collapsible.Content className="border-t border-slate-200 p-3">
              <EligibilityEditor
                accessType="LIBRARY"
                isNew
                originalAccess={originalAccess}
                currentAccess={currentAccess}
                updateHandler={updateEligibility}
              />
            </Collapsible.Content>
          </Collapsible>

          {submitError && <p className="text-sm text-destructive">{submitError}</p>}
        </form>
      </Dialog.Content>

      <Dialog.Footer>
        <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" form={FORM_ID} variant="default" loading={submitting}>
          Publish
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
